import { Dimensions, Position, RotateOptions } from 'src/app/core/types';
import {
  LineSegments,
  Material,
  Mesh,
  MeshBasicMaterial,
  Color,
  BufferGeometry,
  BufferAttribute,
  EdgesGeometry,
  MeshLambertMaterial,
  Group,
  Line,
  DoubleSide,
  Object3D,
  BoxGeometry,
  CylinderGeometry,
  ConeGeometry,
  Vector3,
  CircleGeometry,
  LineLoop,
  LineBasicMaterial,
  TorusGeometry,
  Box3,
} from 'three';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';

export interface Covered {
  coveredDown: boolean;
  coveredLeft: boolean;
  coveredRight: boolean;
  coveredFront: boolean;
  coveredBack: boolean;
}

const newCylinderBufferGeometry = (
  loadType: number,
  diameter: number,
  length: number,
  width: number,
  height: number,
): BufferGeometry => {
  let loadGeometry: BufferGeometry;
  if (loadType === 1) {
    loadGeometry = new CylinderGeometry(diameter / 2, diameter / 2, height, 32).translate(
      diameter / 2,
      height / 2,
      diameter / 2,
    );
  } else if (loadType === 2) {
    const isPipeLength = length >= width;
    const pipeLength = isPipeLength ? length : width;
    loadGeometry = new CylinderGeometry(diameter / 2, diameter / 2, pipeLength, 32, 1, true)
      .translate(isPipeLength ? diameter / 2 : -diameter / 2, pipeLength / 2, -diameter / 2)
      .rotateX(Math.PI / 2);
    if (!isPipeLength) {
      loadGeometry.rotateY(Math.PI / 2);
    }
  } else {
    loadGeometry = new BoxGeometry(width, height, length).translate(
      width / 2,
      height / 2,
      length / 2,
    );
  }
  return loadGeometry;
};

export class Load extends Mesh {
  public oldPosition: Position;
  public oldDimensions: Dimensions;
  public isRotatedPipe: boolean;

  constructor(
    public idGroup: number,
    public loadType: number,
    public name: string,
    public length: number,
    public width: number,
    public height: number,
    public diameter: number,
    public weight: number,
    public yRotation: number,
    public edgeRotation: number,
    public stacking: boolean,
    public color: number,
    public createdAt: number,
    public packed?: number,
    public x?: number,
    public y?: number,
    public z?: number,
    public revert?: boolean,
    public dragged?: boolean,
    public stacked?: boolean,
    public maxLoadsOnTop?: number | string,
    public nameTranslated?: string,
    public showLoadName?: boolean,
    public covered?: Covered,
    public units?: string,
    public loadMeshMaterial?: MeshBasicMaterial[] | MeshLambertMaterial[],
    public loadGeometry?: BufferGeometry,
    public edges?: LineSegmentsGeometry,
    public isShadows?: boolean,
    public unloaded?: boolean,
    public centerOfMass?: Position,
    public rotateOptions?: RotateOptions,
    public isPreview?: boolean,
    public rotationDifference?: Vector3,
    public allowOutOfSpace?: boolean,
    public isPrint?: boolean,
    public boundingBox?: Box3,
    public pctSupport?: number,
    public formation?: number,
    public skipOutline?: boolean,
  ) {
    super(
      loadGeometry
        ? loadGeometry
        : newCylinderBufferGeometry(loadType, diameter, length, width, height),
      loadMeshMaterial
        ? loadMeshMaterial.length > 1
          ? [...loadMeshMaterial]
          : loadMeshMaterial[0]
        : isShadows
        ? new MeshLambertMaterial({ color, side: DoubleSide })
        : new MeshBasicMaterial({ color, side: DoubleSide }),
    );
    this.isRotatedPipe = this.loadType === 2 && this.width > this.length;

    if (this.length === 0 && this.diameter) {
      this.length = this.diameter;
    } else if (this.width === 0 && this.diameter) {
      this.width = this.diameter;
    }

    this.rotationDifference = new Vector3(0, 0, 0);
    this.oldPosition = { x: this.x, y: this.y, z: this.z };
    this.oldDimensions = { width: this.width, height: this.height, length: this.length };
    this.position.set(this.x, this.y, this.z);
    if (!skipOutline) {
      const edgesMaterial = new LineMaterial({
        color: centerOfMass && isPreview ? 0xb5bccb : 0xffffff,
        linewidth: 0.0001,
        transparent: centerOfMass && isPreview ? false : true,
        depthWrite: false,
      });
      let edgesObject: LineSegments;
      if (!edges) {
        const edgesGeometry = new EdgesGeometry(this.geometry);
        const geo = new LineSegmentsGeometry().fromEdgesGeometry(edgesGeometry);
        edgesObject = new LineSegments(geo, edgesMaterial);
      } else {
        edgesObject = new LineSegments(edges, edgesMaterial);
      }
      edgesObject.name = 'LineSegment';
      if (this.loadType === 2 || this.loadType === 1) {
        this.add(this.createCylinderTopBottomCirclesEdges());
      } else {
        this.add(edgesObject);
      }
    }

    if (centerOfMass && isPreview) {
      this.add(this.createCenterOfMass());
    }
  }

  private createCylinderTopBottomCirclesEdges(): Group {
    const pipeTopBottomCirclesEdges = new Group();
    const isPipe = this.loadType === 2;
    if (isPipe && this.isPrint) {
      const torusGeometry = new TorusGeometry(this.diameter / 2 - 100, 100, 2, 62);
      const torusMaterial = new MeshLambertMaterial({
        color: 0xe6e6e6,
      });
      const torusMeshTop = new Mesh(torusGeometry, torusMaterial);
      torusMeshTop.position.set(
        this.isRotatedPipe ? this.width : this.loadType === 1 ? this.height : this.length,
        this.diameter / 2,
        this.diameter / 2,
      );
      torusMeshTop.rotateY(Math.PI / 2);
      pipeTopBottomCirclesEdges.add(torusMeshTop);

      const torusMeshBottom = new Mesh(torusGeometry, torusMaterial);
      torusMeshBottom.position.set(
        this.isRotatedPipe ? this.width : this.loadType === 1 ? this.height : this.length,
        this.diameter / 2,
        this.diameter / 2,
      );
      torusMeshBottom.position.set(0, this.diameter / 2, this.diameter / 2);
      torusMeshBottom.rotateY(Math.PI / 2);
      pipeTopBottomCirclesEdges.add(torusMeshBottom);
    }

    const circleColor = this.isPrint ? 0xe6e6e6 : 0xffffff;
    // Create circle geometry without the center vertex for LineLoop
    const tempCircleGeometry = new CircleGeometry(this.diameter / 2, 256);
    const positions = tempCircleGeometry.attributes.position;
    // Skip first vertex (center point) - copy remaining vertices
    const newPositions = new Float32Array((positions.count - 1) * 3);
    for (let i = 1; i < positions.count; i++) {
      newPositions[(i - 1) * 3] = positions.getX(i);
      newPositions[(i - 1) * 3 + 1] = positions.getY(i);
      newPositions[(i - 1) * 3 + 2] = positions.getZ(i);
    }
    const circleGeometry = new BufferGeometry();
    circleGeometry.setAttribute('position', new BufferAttribute(newPositions, 3));
    tempCircleGeometry.dispose();

    const edgesMaterial = new LineBasicMaterial({
      color: this.centerOfMass && this.isPreview ? 0xb5bccb : circleColor,
    });
    const circleTop = new LineLoop(circleGeometry, edgesMaterial);
    circleTop.position.set(
      this.isRotatedPipe ? this.width : this.loadType === 1 ? this.height : this.length,
      this.diameter / 2,
      this.diameter / 2,
    );
    circleTop.rotateY(Math.PI / 2);
    circleTop.name = 'LineSegment';
    pipeTopBottomCirclesEdges.add(circleTop);
    const circleBottom = new LineLoop(circleGeometry, edgesMaterial);
    circleBottom.position.set(0, this.diameter / 2, this.diameter / 2);
    circleBottom.rotateY(Math.PI / 2);
    circleBottom.name = 'LineSegment';
    pipeTopBottomCirclesEdges.add(circleBottom);
    if (!this.isRotatedPipe) {
      pipeTopBottomCirclesEdges.rotateY(Math.PI / 2);
      pipeTopBottomCirclesEdges.translateX(-this.length);
    }
    if (this.loadType === 1) {
      pipeTopBottomCirclesEdges.translateX(this.diameter);
      pipeTopBottomCirclesEdges.rotateZ(Math.PI / 2);
    }
    pipeTopBottomCirclesEdges.name = 'LineSegments';
    return pipeTopBottomCirclesEdges;
  }

  public x2(): number {
    return this.x + this.width;
  }

  public y2(): number {
    return this.y + this.height;
  }

  public z2(): number {
    return this.z + this.length;
  }

  public setStacked(loads: Load[]): void {
    this.stacked = !!loads.find((load: Load): boolean => {
      if (
        this.loadType === 2 &&
        load.loadType === 2 &&
        this.isRotatedPipe &&
        load.formation === 2
      ) {
        const isUnderY: boolean = this.y2() <= load.y + (Math.sqrt(3) * load.diameter) / 2;
        const isInsideX: boolean = this.x >= load.x && this.x2() <= load.x2();
        const isInsideZ: boolean =
          (this.z >= load.z - load.diameter / 2 && this.z2() <= load.z2() - load.diameter / 2) ||
          (this.z >= load.z + load.diameter / 2 && this.z2() <= load.z2() + load.diameter / 2);
        return isUnderY && isInsideX && isInsideZ;
      } else if (
        this.loadType === 2 &&
        load.loadType === 2 &&
        !this.isRotatedPipe &&
        load.formation === 2
      ) {
        const isUnderY: boolean = this.y2() <= load.y + (Math.sqrt(3) * load.diameter) / 2;
        const isInsideZ: boolean = this.z >= load.z && this.z2() <= load.z2();
        const isInsideX: boolean =
          (this.x >= load.x - load.diameter / 2 && this.x2() <= load.x2() - load.diameter / 2) ||
          (this.x >= load.x + load.diameter / 2 && this.x2() <= load.x2() + load.diameter / 2);
        return isUnderY && isInsideX && isInsideZ;
      } else {
        return (
          this.y2() === load.y &&
          this.x < load.x2() &&
          this.x2() > load.x &&
          this.z < load.z2() &&
          this.z2() > load.z
        );
      }
    });
  }

  public isTransparent(): boolean {
    return (this.material as Material).opacity !== 1;
  }

  public setTransparency(transparent: boolean) {
    (this.material as Material).transparent = transparent;
  }

  public setOpacity(opacity: number) {
    (this.material as Material).opacity = opacity;
  }

  public setColor(color: number) {
    (this.material as MeshBasicMaterial).color = new Color(color);
  }

  public createCenterOfMass() {
    const width = this.width;
    const height = this.height;
    const length = this.length;
    const group = new Group();
    group.name = 'CenterOfMass';
    const offset = 10;

    const greenLineGeo = new LineGeometry();
    const greenLinePositions = [];
    greenLinePositions.push(width / 2, height + offset, 0);
    greenLinePositions.push(width / 2, height + offset, length);
    greenLineGeo.setPositions(greenLinePositions);
    const greenLineMaterial = new LineMaterial({
      color: 0xc2e6d9,
      linewidth: 0.005,
    });
    const greenLine = new Line2(greenLineGeo, greenLineMaterial);

    const redLineGeo = new LineGeometry();
    const redLinePositions = [];
    redLinePositions.push(0, height + offset, length / 2);
    redLinePositions.push(width, height + offset, length / 2);
    redLineGeo.setPositions(redLinePositions);
    const redLineMaterial = new LineMaterial({
      color: 0xe4c9d5,
      linewidth: 0.005,
    });
    const redLine = new Line2(redLineGeo, redLineMaterial);

    group.add(greenLine);
    group.add(redLine);
    const greenLine2Geo = new LineGeometry();
    const greenLine2Positions = [];
    greenLine2Positions.push(width / 2, offset, 0);
    greenLine2Positions.push(width / 2, offset, length);
    greenLine2Geo.setPositions(greenLine2Positions);
    const greenLine2 = new Line2(greenLine2Geo, greenLineMaterial);

    const redLine2Geo = new LineGeometry();
    const redLine2Positions = [];
    redLine2Positions.push(0, offset, length / 2);
    redLine2Positions.push(width, offset, length / 2);
    redLine2Geo.setPositions(redLine2Positions);
    const redLine2 = new Line(redLine2Geo, redLineMaterial);

    group.add(greenLine2);
    group.add(redLine2);
    const gravityArrow = this.drawGravityArrow();
    group.add(gravityArrow);

    return group;
  }

  protected drawGravityArrow(): Object3D {
    const gravityArrowGroup = new Group();
    gravityArrowGroup.name = 'gravityArrowGroup';
    const height = this.height;
    const cogX = this.centerOfMass.x * (this.units === 'cm' || this.units === 'mm' ? 100 : 254);
    const cogY = this.centerOfMass.y * (this.units === 'cm' || this.units === 'mm' ? 100 : 254);
    const cogZ = this.centerOfMass.z * (this.units === 'cm' || this.units === 'mm' ? 100 : 254);
    const positionX = cogX;
    const positionZ = cogZ;
    const cross1Geo = new BoxGeometry(2000, 10, 200);
    const gravityArrowMaterial: MeshBasicMaterial = new MeshBasicMaterial({
      color: 0x525f7f,
      side: DoubleSide,
      transparent: false,
    });
    const cross1Mesh = new Mesh(cross1Geo, gravityArrowMaterial);
    cross1Mesh.position.set(positionX, height, positionZ);
    gravityArrowGroup.add(cross1Mesh);

    const cross2Geo = new BoxGeometry(2000, 10, 200);
    const cross2Mesh = new Mesh(cross2Geo, gravityArrowMaterial);
    cross2Mesh.position.set(positionX, height, positionZ);
    cross2Mesh.rotation.y = Math.PI / 2;
    gravityArrowGroup.add(cross2Mesh);
    let lineHeight = height / 2;
    if (cogY >= 0) {
      lineHeight = height - cogY;
    }
    const lineGeo = new CylinderGeometry(70, 70, lineHeight);
    const lineMesh = new Mesh(lineGeo, gravityArrowMaterial);
    lineMesh.position.set(positionX, height - lineHeight / 2, positionZ);
    gravityArrowGroup.add(lineMesh);

    const tipGeo = new ConeGeometry(400, 400);
    const tipMesh = new Mesh(tipGeo, gravityArrowMaterial);
    tipMesh.position.set(positionX, height - 100 - lineHeight, positionZ);
    tipMesh.rotation.z = Math.PI;
    gravityArrowGroup.add(tipMesh);

    return gravityArrowGroup;
  }
}
